import { ref, type Ref } from 'vue'
import type { Light } from '@/stores/lights'
import { useLightsStore } from '@/stores/lights'
import { lightPresenceService, type LightState } from '@/services/realtime/LightPresenceService'
import { hueBackgroundService } from '@/services/hue/HueBackgroundService'
import { wledBackgroundService } from '@/services/wled/WledBackgroundService'
import { logger } from '@/lib/logger'
import { 
  clampToCircle, 
  xyToHSV, 
  xyToCT, 
  hsvToHueState,
  type XYCoord 
} from '@/lib/colorConversion'

interface CircleDragOptions {
  circleType: 'hue' | 'white'
  currentSceneId: Ref<string | null>
  selectedItems: Ref<Light[]>
  onUpdate?: () => void
}

export interface CircleDragHandler {
  isDragging: Ref<boolean>
  startDrag: () => Promise<void>
  updatePosition: (coord: XYCoord) => void
  endDrag: () => Promise<void>
  broadcastUpdates: () => Promise<void>
  cancelPendingUpdates: (switchingToCircle?: 'hue' | 'white') => Promise<void>
}

export function useCircleDrag(options: CircleDragOptions): CircleDragHandler {
  const { circleType, currentSceneId, selectedItems, onUpdate } = options
  
  const lightsStore = useLightsStore()
  
  const isDragging = ref(false)
  const pendingBroadcasts = ref<Map<string, LightState>>(new Map())
  let broadcastInterval: NodeJS.Timeout | null = null

  /**
   * Start dragging (called on mousedown)
   * Start interval to broadcast state every 500ms during drag
   */
  const startDrag = async () => {
    isDragging.value = true
    
    // Start broadcasting interval (every 500ms during drag)
    startBroadcastInterval()
  }

  /**
   * Update position during drag (called on mousemove)
   */
  const updatePosition = (coord: XYCoord) => {
    if (!isDragging.value || !currentSceneId.value) return

    const clamped = clampToCircle(coord)

    if (circleType === 'hue') {
      updateHuePosition(clamped)
    } else {
      updateWhitePosition(clamped)
    }

    onUpdate?.()
  }

  /**
   * Update hue/saturation for selected lights
   */
  const updateHuePosition = (coord: XYCoord) => {
    const hsv = xyToHSV(coord)
    const hueState = hsvToHueState(hsv)

    logger.info(`[useCircleDrag] Updating HUE position - hue: ${hueState.hue}, sat: ${hueState.saturation}`)

    // Batch state updates to avoid race conditions
    const updates: Array<{ lightId: string; state: LightState }> = []

    for (const light of selectedItems.value) {
      updates.push({
        lightId: light.id,
        state: {
          on: true,
          brightness: light.current_state?.bri ?? 127,
          hue: hueState.hue,
          saturation: hueState.saturation
        }
      })
    }

    // Apply updates synchronously to maintain reactivity during drag
    for (const { lightId, state } of updates) {
      logger.info(`[useCircleDrag] Updating current_state for ${lightsStore.lights.get(lightId)?.name} with HUE mode`)
      
      // Update local state immediately
      lightsStore.updateCurrentState(lightId, {
        hue: state.hue,
        sat: state.saturation
      })
      
      // Queue for presence broadcast
      pendingBroadcasts.value.set(lightId, state)
    }
  }

  /**
   * Update color temperature for selected lights
   */
  const updateWhitePosition = (coord: XYCoord) => {
    const ct = xyToCT(coord)

    logger.info(`[useCircleDrag] Updating WHITE position - ct: ${ct}`)

    // Batch state updates to avoid race conditions
    const updates: Array<{ lightId: string; state: LightState }> = []

    for (const light of selectedItems.value) {
      updates.push({
        lightId: light.id,
        state: {
          on: true,
          brightness: light.current_state?.bri ?? 127,
          ct: ct
        }
      })
    }

    // Apply updates synchronously to maintain reactivity during drag
    for (const { lightId, state } of updates) {
      logger.info(`[useCircleDrag] Updating current_state for ${lightsStore.lights.get(lightId)?.name} with CT mode`)
      
      // Update local state immediately
      lightsStore.updateCurrentState(lightId, {
        ct: state.ct
      })
      
      // Queue for presence broadcast
      pendingBroadcasts.value.set(lightId, state)
    }
  }

  /**
   * Broadcast all pending updates via presence
   */
  const broadcastUpdates = async () => {
    if (pendingBroadcasts.value.size === 0) {
      return
    }

    logger.info(`📡 [useCircleDrag] Broadcasting ${pendingBroadcasts.value.size} state updates via presence`)

    try {
      // Track local changes and broadcast all pending states
      const broadcasts = Array.from(pendingBroadcasts.value.entries()).map(([lightId, state]) => {
        // Track this change in HueBackgroundService (for Hue bridge writes)
        hueBackgroundService.trackLocalStateChange(lightId, state)
        // Track this change in WledBackgroundService (for WLED device writes)
        wledBackgroundService.trackLocalStateChange(lightId, state)
        // Broadcast to other clients
        return lightPresenceService.broadcastLightState(lightId, state)
      })

      await Promise.allSettled(broadcasts)
      logger.info(`✅ [useCircleDrag] Broadcast complete`)
    } catch (error) {
      logger.error('Failed to broadcast updates:', error)
    } finally {
      pendingBroadcasts.value.clear()
    }
  }

  /**
   * Start interval to broadcast updates every 500ms during drag
   */
  const startBroadcastInterval = () => {
    // Clear any existing interval
    if (broadcastInterval) {
      clearInterval(broadcastInterval)
    }

    // Broadcast every 500ms while dragging
    broadcastInterval = setInterval(() => {
      if (isDragging.value && pendingBroadcasts.value.size > 0) {
        logger.info(`⏱️ [useCircleDrag] Interval broadcast (${pendingBroadcasts.value.size} updates)`)
        broadcastUpdates()
      }
    }, 500)
  }

  /**
   * Stop broadcast interval
   */
  const stopBroadcastInterval = () => {
    if (broadcastInterval) {
      clearInterval(broadcastInterval)
      broadcastInterval = null
    }
  }

  /**
   * End dragging and broadcast final updates
   */
  const endDrag = async () => {
    logger.info(`[useCircleDrag] End drag called, isDragging: ${isDragging.value}`)
    
    // Stop the broadcast interval
    stopBroadcastInterval()
    
    if (isDragging.value) {
      // Broadcast any remaining updates
      await broadcastUpdates()
    }
    isDragging.value = false
  }

  /**
   * Cancel pending broadcasts without sending
   * Used when switching circles - broadcasts first WITH mode clearing, then clears
   */
  const cancelPendingUpdates = async (switchingToCircle?: 'hue' | 'white') => {
    const count = pendingBroadcasts.value.size
    
    if (count > 0 && switchingToCircle) {
      logger.info(`🔄 [useCircleDrag] Broadcasting ${count} pending updates before switching to ${switchingToCircle}`)
      
      // When switching circles, explicitly clear the conflicting color mode
      const updatesWithModeClearing = Array.from(pendingBroadcasts.value.entries()).map(([lightId, state]) => {
        const clearedState = { ...state }
        
        if (switchingToCircle === 'white') {
          // Switching to white (CT) mode - clear color fields
          clearedState.hue = undefined
          clearedState.saturation = undefined
          clearedState.xy = undefined
          logger.info(`  ⚪ Clearing hue/sat for ${lightsStore.lights.get(lightId)?.name} (switching to CT)`)
          
          // Also update local store immediately
          lightsStore.updateCurrentState(lightId, {
            hue: undefined,
            sat: undefined,
            xy: undefined
          })
        } else if (switchingToCircle === 'hue') {
          // Switching to color (hue/sat) mode - clear CT field
          clearedState.ct = undefined
          logger.info(`  🎨 Clearing CT for ${lightsStore.lights.get(lightId)?.name} (switching to color)`)
          
          // Also update local store immediately
          lightsStore.updateCurrentState(lightId, {
            ct: undefined
          })
        }
        
        return { lightId, state: clearedState }
      })
      
      // Broadcast with cleared conflicting fields
      const broadcasts = updatesWithModeClearing.map(({ lightId, state }) => 
        lightPresenceService.broadcastLightState(lightId, state)
      )
      
      await Promise.allSettled(broadcasts)
      logger.info(`✅ [useCircleDrag] Broadcast complete with mode clearing`)
    } else if (count > 0) {
      // No circle switch - just broadcast normally
      logger.info(`🔄 [useCircleDrag] Broadcasting ${count} pending updates`)
      await broadcastUpdates()
    }
    
    pendingBroadcasts.value.clear()
    logger.info(`❌ [useCircleDrag] Cleared pending broadcasts`)
  }

  return {
    isDragging,
    startDrag,
    updatePosition,
    endDrag,
    broadcastUpdates,
    cancelPendingUpdates
  }
}
